
@if (isLoading) {
  <div style="display: flex; justify-content: center; align-items: center; height: 25vh;">
    <mat-spinner diameter="50"></mat-spinner>
  </div>
} @else if (hasError) {
  <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 25vh; gap: 1.5rem; padding: 2rem; text-align: center;">
    <mat-icon style="font-size: 64px; color: #f44336;">error_outline</mat-icon>
    <div>
      <h3 style="margin: 0 0 0.5rem 0; color: #f44336; font-size: 1.5rem;">Unable to Load Script</h3>
      <p style="margin: 0; color: #666; max-width: 500px; line-height: 1.5;">{{ errorMessage }}</p>
    </div>
    <div style="display: flex; gap: 1rem; flex-wrap: wrap; justify-content: center;">
      <button mat-raised-button  (click)="retryLoadScript()">
        <mat-icon>refresh</mat-icon>
        Try Again
      </button>
      <button mat-stroked-button  (click)="goToScripts()">
        <mat-icon>home</mat-icon>
        Go to Scripts
      </button>
    </div>
  </div>
} @else if (script) {
  <div style="display: flex; flex-direction: row; gap: 1rem; flex-wrap: wrap;">
    @if (script.id) {
        <!-- Only show status card if the script has an id (not a new script) -->
        <mat-card style="min-width: 400px; flex-grow: 1">
            <mat-card-header style="width: 100%;">

                <mat-card-title style="width: calc(100% - 2rem);">
                    <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                        Current Status: {{scriptService.getStatusText(script).title}}
                        @if (scriptService.canDeleteScript(script)) {
    
                            <button mat-icon-button [matMenuTriggerFor]="scriptMenu">
                                <mat-icon>more_vert</mat-icon>
                            </button>
                            <mat-menu #scriptMenu="matMenu">
                                <button mat-menu-item (click)="openDeleteScriptDialog()">
                                    <mat-icon>delete</mat-icon>
                                    <span>Delete Script</span>
                                </button>
                            </mat-menu>
                        }
                    </div>
                </mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <span>{{scriptService.getStatusText(script).description}}</span>
                
                <!-- Display staff comment if script is rejected and comment exists -->
                @if (script.staffComment) {
                    <div [style]="isScriptRejected(script) ? {
                        'margin-top': '1rem',
                        'padding': '0.75rem',
                        'background-color': 'var(--mat-sys-error-container)',
                        'border-left': '3px solid var(--mat-sys-on-error-container)',
                        'color': 'var(--mat-sys-on-error-container)'
                    } : {
                        'margin-top': '1rem',
                        'padding': '0.75rem', 
                        'background-color': 'var(--mat-sys-tertiary-container)',
                        'border-left': '3px solid var(--mat-sys-on-tertiary-container)',
                        'color': 'var(--mat-sys-on-tertiary-container)'
                    }">
                        <strong>Jar's Comment:</strong>
                        <p style="margin: 0.5rem 0 0 0; white-space: pre;">{{script.staffComment}}</p>
                    </div>
                }
                
                <!-- Display author comment if it exists -->
                @if (script.authorComment) {
                    <div style="
                        margin-top: 1rem; 
                        padding: 0.75rem; 
                        background-color: var(--mat-sys-tertiary-container); 
                        border-left: 3px solid var(--mat-sys-on-tertiary-container); 
                        color: var(--mat-sys-on-tertiary-container);">
                        <strong>Your Comment:</strong>
                        <p style="margin: 0.5rem 0 0 0;">{{script.authorComment}}</p>
                    </div>
                }
            </mat-card-content>
        </mat-card>
    }
    

    <mat-card style="min-width: 100%;">
        <mat-card-header>
            <mat-card-title>Basic Information</mat-card-title>
            <mat-card-subtitle>Tell us about the basics of your script</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <mat-action-list>
                <mat-divider></mat-divider>
                <app-list-item-button 
                    title="Title" 
                    [value]="script.title || ''"
                    [disabled]="readonly"
                    (clicked)="openScriptNameDialog()">
                </app-list-item-button>
                <mat-divider></mat-divider>
                <mat-divider></mat-divider>
                <app-list-item-button 
                    title="Categories" 
                    [value]="script.categories.join(', ') || ''"
                    [disabled]="readonly"
                    (clicked)="openCategoriesDialog()">
                </app-list-item-button>
                <mat-divider></mat-divider>
                <app-list-item-button 
                    title="Backdrop" 
                    [value]="script.backdrop.name || ''"
                    [disabled]="readonly"
                    (clicked)="openBackdropDialog()">
                </app-list-item-button>
            </mat-action-list>
        </mat-card-content>
    </mat-card>
    
    <mat-card style="min-width: 400px; flex-grow: 1;">
        <mat-card-header>
            <mat-card-title>Characters</mat-card-title>
            <mat-card-subtitle>Define what characters will act in your script</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <mat-action-list>
                @for (character of script.characters || []; track character.tag) {
                    <mat-divider></mat-divider>
                    <app-list-item-button 
                        [title]="character.tag + ' - ' + character.name" 
                        [value]="character.location.name"
                        [disabled]="readonly"
                        (clicked)="openCharacterDialog(character)">
                    </app-list-item-button>
                }
                <mat-divider></mat-divider>
                <app-list-item-button 
                    title="Add Character" 
                    icon="add_circle"
                    [disabled]="readonly || (script.characters || []).length >= 6"
                    [value]="(script.characters || []).length >= 6 ? 'Maximum reached' : ''"
                    (clicked)="openCharacterDialog()">
                </app-list-item-button>
            </mat-action-list>
        </mat-card-content>
    </mat-card>

    <mat-card style="min-width: 400px; flex-grow: 1;">
        <mat-card-header>
            <mat-card-title>Suggestions</mat-card-title>
            <mat-card-subtitle>Let the Audience fill in the blanks</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <mat-action-list>
                @for (suggestion of script.suggestions || []; track suggestion.tag) {
                    <mat-divider></mat-divider>
                    <app-list-item-button 
                        [value]="suggestion.description" 
                        [title]="suggestion.tag + ' - ' + suggestion.examples.length + ' examples'"
                        [disabled]="readonly"
                        (clicked)="openSuggestionDialog(suggestion)">
                    </app-list-item-button>
                }
                <mat-divider></mat-divider>
                <app-list-item-button 
                    title="Add Suggestion" 
                    [value]="(script.suggestions || []).length >= 12 ? 'Maximum reached' : ''"
                    icon="add_circle"
                    (clicked)="openSuggestionDialog()"
                    [disabled]="readonly || (script.suggestions || []).length >= 12">
                </app-list-item-button>
            </mat-action-list>
        </mat-card-content>
    </mat-card>

    <mat-card style="min-width: 100%; ">
        <mat-card-header style="margin-bottom: 12px;">
            <div style="width: 100%; display: flex; flex-direction: row; gap: 0.5rem; justify-content: space-between;">
                <mat-card-title>
                    Script
                </mat-card-title>
                <button mat-button (click)="openSoundDialog()" [disabled]="readonly">
                    <app-icon icon="music_note"></app-icon>
                    Browse Sounds
                </button>
            </div>
        </mat-card-header>
        <mat-card-content>
            <app-rich-text-editor 
                [content]="scriptContent"
                [characters]="script.characters || []"
                [suggestions]="script.suggestions || []"
                [sounds]="sounds"
                [universal_locations]="universal_locations"
                [backdrop]="script.backdrop || null"
                [scriptTitle]="script.title || ''"
                [readonly]="readonly"
                (contentChange)="onScriptContentChange($event)">
            </app-rich-text-editor>

            <div style="width: 100%; text-align: right; margin-top: 12px;" class="osv">
                Need help? 
                <a mat-button href="/scripts/tutorial" target="_blank">
                    <app-icon icon="school"></app-icon>
                    Open Tutorial
                </a>
            </div>
        </mat-card-content>
    </mat-card>
  </div>
}    
